import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    duplicateLedgerBodySchema,
    getLedgersByEntityIdsQuerySchema,
    getLedgersForEntityQuerySchema,
    LEDGER_NAME_MAX_LENGTH,
    setLedgerStatusBodySchema,
    updateLedgerBodySchema,
} from "../schemas/domains/accounting/ledger.schema";
import {
    AccountingLedgerBasis,
    AccountingLedgerStatus,
} from "../types/domains/accounting/ledger";

const c = initContract();
export { LEDGER_NAME_MAX_LENGTH };

export interface Ledger {
    id: string;
    entityId: number;
    basis: AccountingLedgerBasis;
    name: string;
    isPrimary: boolean;
    status: AccountingLedgerStatus;
    createdAt: Date;
    createdByUserId: number | null;
    hasJournalEntries?: boolean;
}

export const ledgerContract = c.router(
    {
        getLedgersByEntityIds: {
            method: "GET",
            path: "/",
            query: getLedgersByEntityIdsQuerySchema,
            responses: {
                200: c.type<{
                    ledgers: Record<number, Ledger[]>;
                }>(),
                400: ErrorMessageSchema,
            },
        },
        getLedgersForEntity: {
            method: "GET",
            path: "/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            query: getLedgersForEntityQuerySchema,
            responses: {
                200: c.type<{
                    ledgers: Ledger[];
                }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createLedgerForEntity: {
            method: "PUT",
            path: "/:entityId",
            pathParams: z.object({
                entityId: z.coerce.number(),
            }),
            responses: {
                200: c.type<{
                    ledger: Ledger | null;
                }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: z.object({
                basis: z.nativeEnum(AccountingLedgerBasis),
                name: z
                    .string()
                    .trim()
                    .min(1)
                    .max(LEDGER_NAME_MAX_LENGTH)
                    .optional(),
                isPrimary: z.boolean().optional(),
            }),
        },
        updateLedger: {
            method: "PATCH",
            path: "/:entityId/:ledgerId",
            pathParams: z.object({
                entityId: z.coerce.number(),
                ledgerId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    ledger: Ledger;
                }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: updateLedgerBodySchema,
        },
        setLedgerStatus: {
            method: "POST",
            path: "/:entityId/:ledgerId/status",
            pathParams: z.object({
                entityId: z.coerce.number(),
                ledgerId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    ledger: Ledger;
                }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: setLedgerStatusBodySchema,
        },
        duplicateLedger: {
            method: "POST",
            path: "/:entityId/:ledgerId/duplicate",
            pathParams: z.object({
                entityId: z.coerce.number(),
                ledgerId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    ledger: Ledger;
                }>(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: duplicateLedgerBodySchema,
        },
    },
    {
        pathPrefix: "/accounting/ledgers",
    },
);
